import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';

import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrService } from '@services/toaster.service';
import { startWith } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { QuestionnaireSchema, QuestionnaireSection, QuestionnaireTab } from '@mds/common';
import { ButtonComponent, ButtonToggleComponent, getFieldType } from '@mds/ui';
import { FormlyModule } from '@ngx-formly/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgxMasonryModule } from 'ngx-masonry';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuService } from '@services/menu.service';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MdsIcon } from '@mds/icons';
import { TableHeaderComponent } from '@components/table-header/table-header.component';
import { InformationRequestService } from '@pages/information-requests/information-request.service';
import { RequestHistoryComponent } from '@components/request-history/request-history.component';
import { CampaignTabsEnum } from '@pages/data-collection/helpers/data-collection-tabs';

const DISCUSSION_TAB_KEY = 'initial_thread';
const PRODUCT_INFO_KEY = 'product_info';

interface TabData {
  value: string;
  icon: MdsIcon;
  label: string;
  disabled: boolean;
  ariaLabel: string;
}

@Component({
  selector: 'app-cbam-questionnaire',
  standalone: true,
  imports: [
    FormlyModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatExpansionModule,
    NgxMasonryModule,
    ReactiveFormsModule,
    ButtonToggleComponent,
    ButtonComponent,
    TableHeaderComponent,
    RequestHistoryComponent
  ],
  templateUrl: './cbam-questionnaire.component.html',
  styleUrls: ['./cbam-questionnaire.component.scss']
})
export class CbamQuestionnaireComponent implements OnInit {
  tabs: TabData[];
  icons = MdsIcon;
  selectedTab!: string;
  @Input() data: any;
  @Input() rid: string;
  tab: QuestionnaireTab;
  sectionForms: FormGroup[] = [];
  sectionTypes = [];
  panelOpenState = [true];
  historyTabs = [
    { label: 'Questionnaire', value: CampaignTabsEnum.QUESTIONNAIRES, icon: MdsIcon.CAMPAIGNS },
    { label: 'History', value: CampaignTabsEnum.HISTORY, icon: MdsIcon.HISTORY }
  ];
  historySelectedTab: CampaignTabsEnum = CampaignTabsEnum.QUESTIONNAIRES;
  campaignTabs = CampaignTabsEnum;
  @Output() formValid = new EventEmitter<boolean>();
  private toastr = inject(ToastrService);
  private informationRequestService = inject(InformationRequestService);
  private destroyRef = inject(DestroyRef);
  private menuService = inject(MenuService);

  isUserExternalSupplier = this.menuService.isExternalSupplier();

  ngOnInit(): void {
    this.getTabs();
    this.menuService.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((reload) => {
      if (reload) {
        this.initTab();
      }
    });
  }

  onSubmit(index: number): void {
    const payload = this.createPayloadData(index);

    const target = this.tab.sections?.[index]?.target ? this.tab.sections[index].target : `composer/route/${this.rid}`;

    this.informationRequestService
      .updateInformationRequest(target, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastr.showMessage('Answers successfully saved', 1000);
          this.updateDefaultValue(this.sectionForms[index].getRawValue(), index);
          this.initTab();
        },
        error: (error) => this.showError(error)
      });
  }

  isFormsValid() {
    const allFormsValid = this.sectionForms?.every((val: FormGroup) => val.valid);
    return this.formValid.emit(allFormsValid);
  }

  onSwitchView(tab: MatButtonToggleChange) {
    this.selectedTab = tab.value;
    this.initTab();
  }

  private getTabs() {
    if (!this.data?.tabs?.length) return;
    this.selectedTab = this.data.tabs[0].key;
    this.tabs = this.data.tabs
      .filter((tab: any) => tab.key !== DISCUSSION_TAB_KEY)
      .map((tab: QuestionnaireTab) => ({
        value: tab.key,
        icon: tab.key === 'site_info' ? MdsIcon.FACTORY : MdsIcon.PRODUCT_INFORMATION,
        label: tab.name,
        disabled: false,
        ariaLabel: 'Font Normal'
      }));
    this.initTab();
  }

  private createPayloadData(index: number) {
    return this.tab.key === PRODUCT_INFO_KEY
      ? {
          formData: [this.sectionForms[index].getRawValue()],
          key: this.rid
        }
      : this.sectionForms[index].getRawValue();
  }

  /**
   * Updates the default values in the form schema with the given form values.
   * Updates both the source data (this.data.tabs) and the current tab clone
   * so that subsequent initTab() calls preserve the saved values.
   * @param formValues - The values from the submitted form.
   * @param index - The index of the section to update.
   */
  private updateDefaultValue(formValues: Record<string, any>, index: number): void {
    const sourceTab = this.data.tabs.find((tab: QuestionnaireTab) => tab.key === this.selectedTab);
    const targets = [this.tab.sections[index].formSchema, sourceTab?.sections?.[index]?.formSchema].filter(Boolean);

    targets.forEach((formSchema: QuestionnaireSchema[]) => {
      formSchema.forEach((e) => {
        const keys = e.key.split('.');
        const newValue = keys.length > 1 ? formValues[keys[0]][keys[1]] : formValues[keys[0]];
        if (e.defaultValue !== newValue) {
          e.defaultValue = newValue;
        }
      });
    });
  }

  private initTab() {
    this.tab = structuredClone(this.data.tabs.filter((tab: QuestionnaireTab) => tab.key === this.selectedTab))[0];
    if (!this.tab.isGrid) {
      this.sectionForms = [];
      this.sectionTypes = [];
      this.panelOpenState = [];
      this.tab.sections.forEach((section) => {
        const sectionForm = new FormGroup({});
        const { types } = this.createSectionFieldTypes(section);
        this.sectionTypes.push(types);
        this.sectionForms.push(sectionForm);
        sectionForm.valueChanges.pipe(startWith({})).subscribe(() => {
          this.isFormsValid();
        });
      });
    }
  }

  private createSectionFieldTypes(section: QuestionnaireSection) {
    const types = section.formSchema.map((form: QuestionnaireSchema) => {
      let hideExpressionData = null;
      // hide field based on another field data
      if (form.hide_options_key) {
        hideExpressionData =
          form.hide_options_key && form.hide_options_value !== null && form.hide_options_value !== undefined
            ? `model.${form.hide_options_key} === ${form.hide_options_value}`
            : null;
      }
      return {
        key: form.key,
        defaultValue: form.defaultValue,
        type: getFieldType(form.key, form.fieldtype, form.formtype),
        hideExpression: hideExpressionData,
        templateOptions: {
          label: form.question,
          info: form.question_help,
          placeholder: form.placeholder,
          disabled: !section.actions.edit || false,
          suffix: form.suffix,
          prefix: form.prefix,
          type: form.fieldtype,
          required: form.mandatory,
          options: ['radio', 'select'].includes(form.formtype) ? form.enum : []
        }
      };
    });
    return { types };
  }

  private showError(err: HttpErrorResponse) {
    this.toastr.showMessage(err?.error?.message ?? 'Something went wrong', 1000);
  }

  onSwitchViewHistory(selectedTab: { label: string; value: CampaignTabsEnum }): void {
    this.historySelectedTab = selectedTab.value;
  }
}
